import { useMemo } from "react";
import dayjs from "dayjs";
import { Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/EmptyState";
import { StatusPill } from "@/components/StatusPill";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { fmtDate } from "@/lib/format";

const BAR = {
  NOT_STARTED: "bg-slate-400 dark:bg-slate-500",
  IN_PROGRESS: "bg-blue-500",
  COMPLETED: "bg-emerald-500",
  DELAYED: "bg-rose-500",
};
const LABEL_W = 270; // px, left column
const MIN_W = 880;   // px, timeline scrolls sideways below this

const pct = (v) => Math.min(Math.max(Number(v) || 0, 0), 1);

export default function GanttChart({ tasks, loading, isProjectManager, onEdit, onDelete }) {
  const today = dayjs().startOf("day");

  const range = useMemo(() => {
    const ds = tasks
      .flatMap((t) => [t.start_date, t.end_date, t.actual_start_date, t.actual_end_date])
      .filter(Boolean)
      .map((d) => dayjs(d));
    if (ds.length === 0) return null;
    let lo = ds.reduce((a, b) => (a.isBefore(b) ? a : b));
    let hi = ds.reduce((a, b) => (a.isAfter(b) ? a : b));
    // keep "today" on the chart when it is close to the plan (within 90 days)
    if (today.isBefore(lo) && lo.diff(today, "day") <= 90) lo = today;
    if (today.isAfter(hi) && today.diff(hi, "day") <= 90) hi = today;
    const start = lo.startOf("month");
    const end = hi.endOf("month").add(1, "day").startOf("day");
    return { start, end, days: Math.max(end.diff(start, "day"), 1) };
  }, [tasks]); // eslint-disable-line react-hooks/exhaustive-deps

  const months = useMemo(() => {
    if (!range) return [];
    const out = [];
    let m = range.start;
    while (m.isBefore(range.end)) { out.push(m); m = m.add(1, "month"); }
    return out;
  }, [range]);

  const x = (d) => (range ? (d.diff(range.start, "day") / range.days) * 100 : 0);
  const w = (a, b) => (range ? (Math.max(b.diff(a, "day") + 1, 1) / range.days) * 100 : 0);
  const step = Math.max(Math.ceil(months.length / 14), 1);

  const dated = tasks.filter((t) => t.start_date && t.end_date);
  const overall = tasks.length ? tasks.reduce((s, t) => s + pct(t.progress_pct), 0) / tasks.length : 0;
  const counts = {
    done: tasks.filter((t) => t.status === "COMPLETED").length,
    active: tasks.filter((t) => t.status === "IN_PROGRESS").length,
    late: tasks.filter((t) => t.status === "DELAYED").length,
  };
  const todayX = range && !today.isBefore(range.start) && today.isBefore(range.end) ? x(today) : null;

  return (
    <div className="rounded-xl border border-border bg-card" data-testid="gantt-chart-container">
      {!loading && tasks.length === 0 ? (
        <div className="p-4">
          <EmptyState title="No schedule tasks yet" description={isProjectManager ? "Press Build from WBS to create one milestone per construction phase, with dates and progress taken from its activities." : undefined} testId="schedule-empty-state" />
        </div>
      ) : (
        <>
          {/* summary + legend */}
          <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2 border-b border-border px-4 py-3">
            <div className="flex items-center gap-3">
              <span className="text-xs text-muted-foreground">Overall progress</span>
              <div className="h-2 w-40 overflow-hidden rounded-full bg-muted">
                <div className="h-full rounded-full bg-blue-500" style={{ width: `${overall * 100}%` }} />
              </div>
              <span className="font-mono text-xs text-foreground" data-testid="gantt-overall">{Math.round(overall * 100)}%</span>
              <span className="text-xs text-muted-foreground">
                {counts.done} done · {counts.active} in progress{counts.late ? <> · <span className="text-rose-500">{counts.late} delayed</span></> : null}
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-4 text-[11px] text-muted-foreground">
              <span className="flex items-center gap-1.5"><i className="inline-block h-2.5 w-5 rounded-sm bg-blue-500/30" />Planned</span>
              <span className="flex items-center gap-1.5"><i className="inline-block h-2.5 w-5 rounded-sm bg-blue-500" />Progress</span>
              <span className="flex items-center gap-1.5"><i className="inline-block h-1.5 w-5 rounded-sm bg-foreground/70" />Actual</span>
              <span className="flex items-center gap-1.5"><i className="inline-block h-3 w-0.5 bg-rose-500" />Today</span>
            </div>
          </div>

          <div className="overflow-x-auto">
            <div style={{ minWidth: MIN_W }}>
              {/* month header */}
              <div className="flex border-b border-border bg-muted/40 text-[11px] text-muted-foreground">
                <div className="shrink-0 px-4 py-2 font-medium uppercase tracking-wider" style={{ width: LABEL_W }}>Milestone</div>
                <div className="relative h-8 flex-1">
                  {months.map((m, i) => (
                    <div key={m.format("YYYY-MM")} className="absolute inset-y-0 flex items-center border-l border-border pl-1.5 font-medium" style={{ left: `${x(m)}%` }}>
                      {i % step === 0 ? m.format(months.length > 12 ? "MMM YY" : "MMM YYYY") : ""}
                    </div>
                  ))}
                </div>
              </div>

              <div className="relative">
                {/* gridlines + today line behind the rows */}
                {range && (
                  <div className="pointer-events-none absolute inset-y-0 right-0" style={{ left: LABEL_W }}>
                    {months.map((m) => (
                      <div key={m.format("YYYY-MM")} className="absolute inset-y-0 border-l border-border/60" style={{ left: `${x(m)}%` }} />
                    ))}
                    {todayX !== null && (
                      <div className="absolute inset-y-0 z-10" style={{ left: `${todayX}%`, width: 2, marginLeft: -1, backgroundColor: "#e11d48" }} data-testid="gantt-today-line">
                        <span className="absolute -top-px left-1/2 -translate-x-1/2 rounded-b bg-rose-500 px-1 text-[9px] font-medium leading-4 text-white">Today</span>
                      </div>
                    )}
                  </div>
                )}

                {tasks.map((t) => {
                  const dep = tasks.find((d) => d.id === t.depends_on_id);
                  const hasPlan = Boolean(t.start_date && t.end_date);
                  const ps = hasPlan ? dayjs(t.start_date) : null;
                  const pe = hasPlan ? dayjs(t.end_date) : null;
                  const p = pct(t.progress_pct);
                  const as = t.actual_start_date ? dayjs(t.actual_start_date) : null;
                  const ae = t.actual_end_date ? dayjs(t.actual_end_date) : as && t.status !== "COMPLETED" ? (today.isBefore(as) ? as : today) : as;
                  const overdue = hasPlan && t.status !== "COMPLETED" && pe.isBefore(today);
                  const color = BAR[t.status] || BAR.NOT_STARTED;
                  const tip = `${t.name}\nPlanned: ${fmtDate(t.start_date)} → ${fmtDate(t.end_date)}${as ? `\nActual: ${fmtDate(t.actual_start_date)} → ${t.actual_end_date ? fmtDate(t.actual_end_date) : "ongoing"}` : ""}\nProgress: ${Math.round(p * 100)}%`;
                  return (
                    <div key={t.id} data-testid={`gantt-row-${t.id}`} className="group relative flex border-b border-border/60 last:border-b-0 hover:bg-muted/30">
                      <div className="relative z-20 shrink-0 px-4 py-2.5" style={{ width: LABEL_W }}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <div className="truncate text-sm font-medium text-foreground" title={t.name}>{t.name}</div>
                            <div className="mt-1 flex flex-wrap items-center gap-1.5">
                              <StatusPill value={t.status} />
                              <span className="font-mono text-[11px] text-muted-foreground" data-testid={`gantt-progress-${t.id}`}>{Math.round(p * 100)}%</span>
                            </div>
                            <div className="mt-1 text-[11px] text-muted-foreground">
                              {hasPlan ? `${fmtDate(t.start_date)} → ${fmtDate(t.end_date)}` : "No planned dates"}
                              {overdue && <span className="ml-1 text-rose-500">· past end date</span>}
                            </div>
                            {dep && <div className="truncate text-[11px] text-muted-foreground">↳ after {dep.name}</div>}
                          </div>
                          {isProjectManager && (
                            <div className="flex shrink-0 items-center opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
                              <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => onEdit(t)} data-testid={`edit-schedule-task-btn-${t.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                              <AlertDialog>
                                <AlertDialogTrigger asChild><Button size="sm" variant="ghost" className="h-7 w-7 p-0" data-testid={`delete-schedule-task-btn-${t.id}`}><Trash2 className="h-3.5 w-3.5 text-rose-400" /></Button></AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader><AlertDialogTitle>Delete task?</AlertDialogTitle><AlertDialogDescription>{t.name}</AlertDialogDescription></AlertDialogHeader>
                                  <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => onDelete(t.id)}>Delete</AlertDialogAction></AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="relative min-h-[76px] flex-1">
                        {hasPlan && range ? (
                          <>
                            <div className="absolute top-4 h-6" style={{ left: `${x(ps)}%`, width: `${w(ps, pe)}%`, minWidth: 6 }} title={tip}>
                              <div className={`absolute inset-0 rounded ${color} opacity-30`} />
                              <div className={`absolute inset-y-0 left-0 rounded ${color}`} style={{ width: `${p * 100}%` }} />
                              <div className="absolute inset-0 flex items-center px-1.5 text-[10px] font-medium text-foreground/80">{p > 0 && p < 1 ? `${Math.round(p * 100)}%` : ""}</div>
                            </div>
                            {as && (
                              <div className="absolute top-[46px] h-1.5 rounded-full bg-foreground/70" style={{ left: `${x(as)}%`, width: `${w(as, ae)}%`, minWidth: 6 }} title={tip} />
                            )}
                          </>
                        ) : (
                          <div className="flex h-full items-center px-4 text-xs text-muted-foreground">
                            {isProjectManager ? "Set start and end dates (edit this task) to place it on the timeline." : "No dates set"}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          {dated.length === 0 && tasks.length > 0 && (
            <div className="border-t border-border px-4 py-2 text-xs text-muted-foreground">None of these tasks has planned dates yet, so there are no bars to draw.</div>
          )}
        </>
      )}
    </div>
  );
}
